import { API_BASE_URL } from "src/config/api";
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { TextField, Button, Container, Typography, Paper, Grid, Box, Card, CircularProgress, Dialog } from '@mui/material';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import AddBalanceIcon from '@mui/icons-material/AddCircleOutline';
import WalletHistoryIcon from '@mui/icons-material/History';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import ReactConfetti from 'react-confetti';
import { styled } from '@mui/material/styles';
import ConfirmationDialog from '../../../layouts/shared/ConfirmationDialog';
import { Payment } from '../razorpayPayment';


const StyledCard = styled('div')(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'light'
      ? theme.palette.grey[100]
      : theme.palette.background.paper,

  padding: theme.spacing(2.5),
  borderRadius: 20,

  boxShadow:
    theme.palette.mode === 'light'
      ? '0px 0px 10px rgba(0, 0, 0, 0.15)'
      : '0px 0px 10px rgba(0, 0, 0, 0.6)',
}));

function PurchaseCustomizeFormPage(props) {
  PurchaseCustomizeFormPage.propTypes = {
    closePurchaseCustomizeForm: PropTypes.func.isRequired,
  };
  const [walletAmount, setWalletAmount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [subsciptionPlanId, setSubsciptionPlanId] = useState(null);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const openForm = () => {
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
  };

  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');

  const { _id, subscriptionName, subscriptionAmount } = useParams();
  const [isConfettiActive, setConfettiActive] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };

        const response = await axios.get(`${API_BASE_URL}/getWalletBalance?userId=${userId}`, config);

        const walletAmount = response.data.balance;
        setWalletAmount(walletAmount);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUser();
  }, [userId, token, _id, subscriptionName, subscriptionAmount]);

  useEffect(() => {
    const fetchCustomizedPlans = async () => {
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };

        const response = await axios.get(
          `${API_BASE_URL}/getCustomizedPlansByUserId?userId=${userId}`,
          config
        );

        const customizeData = response.data.data;
        setSubsciptionPlanId(customizeData[0].subsciptionPlanId);
      } catch (error) {
        console.error('Error fetching Customized Plan data:', error);
      }
    };

    fetchCustomizedPlans();
  }, [userId, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsConfirmationDialogOpen(true);
  };

  const handleConfirmBuy = async (e) => {
    setIsConfirmationDialogOpen(false);
    e.preventDefault();
    setIsLoading(true);
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      if (_id) {
        const updatedFormData = {
          userId,
          subsciptionPlanId,
          customizedPlanId: _id,
        };

        const response = await axios.post(
          `${API_BASE_URL}/purchaseSubscription`,
          updatedFormData,
          config
        );
        if (response.status === 200) {
          setConfettiActive(true);
          toast.success(response.data.message);
        } else {
          toast.error(response.data.message);
        }
        setTimeout(() => {
          setConfettiActive(false);
          props.closePurchaseCustomizeForm();
        }, 3000);
      } else {
        toast.error('Invalid URL format.');
      }
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };
  const handleCancelBuy = () => {
    setIsConfirmationDialogOpen(false);
  };

  return (
    <Card>
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          p: 2,
          borderRadius: 1,
        }}
      >
        <Typography variant="h6">Wallet Balance</Typography>
        <Typography variant="h4" sx={{ ml: 1 }}>
          {walletAmount !== null ? `$${walletAmount}` : 'N/A'}
        </Typography>
      </Box>
      <Container maxWidth="sm">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '10px',
            marginBottom: '10px',
            marginTop: '20px',
          }}
        >
          <Box
            role="button"
            tabIndex={0}
            onClick={openForm}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
              borderRadius: 2,
              cursor: 'pointer',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: 'action.hover',
                transform: 'translateY(-5px)',
                borderColor: 'primary.main',
              },
            }}
          >
            <AddBalanceIcon color="primary" />
            <Typography variant="h6" color="text.primary">
              Add Balance
            </Typography>
          </Box>

          <Dialog open={isFormOpen} onClose={closeForm}>
            <StyledCard>
              <Payment open={openForm} closeForm={closeForm} />
            </StyledCard>
          </Dialog>

          <a href="/dashboard/wallet" style={{ textDecoration: 'none', color: 'inherit' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
                borderRadius: 2,
                cursor: 'pointer',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transform: 'translateY(-5px)',
                  borderColor: 'primary.main',
                },
              }}
            >
              <Typography>
                <WalletHistoryIcon color="primary" />
              </Typography>
              <Typography variant="h6" color="text.primary">
                Wallet History
              </Typography>
            </Box>
          </a>
        </div>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            my: 3,
          }}
        >
          <WorkspacePremiumIcon
            sx={{
              fontSize: 110,
              color: 'primary.main',
              opacity: 0.9,
            }}
          />
        </Box>

        <Paper elevation={3} style={{ padding: '2rem' }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1">Subscription Name</Typography>
                <TextField
                  fullWidth
                  value={subscriptionName || ''}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputBase-root': {
                      bgcolor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? theme.palette.action.disabledBackground
                          : theme.palette.grey[100],
                    },
                    '& .MuiInputBase-input': {
                      cursor: 'not-allowed',
                      color: 'text.secondary',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1">Amount ($)</Typography>
                <TextField
                  fullWidth
                  value={subscriptionAmount || ''}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputBase-root': {
                      bgcolor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? theme.palette.action.disabledBackground
                          : theme.palette.grey[100],
                    },
                    '& .MuiInputBase-input': {
                      cursor: 'not-allowed',
                      color: 'text.secondary',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                  {isLoading ? (
                    <CircularProgress />
                  ) : (
                    <>
                      <Button variant="contained" type="submit" color="primary">
                        Buy Plan
                      </Button>
                      <Button
                        variant="outlined"
                        color='error'
                        onClick={() => props.closePurchaseCustomizeForm()}
                        style={{ marginLeft: '1rem' }}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                  <ConfirmationDialog
                    isOpen={isConfirmationDialogOpen}
                    onClose={handleCancelBuy}
                    onConfirm={handleConfirmBuy}
                    title='Confirm Purchase'
                    message="Are you sure you want to Buy?"
                  />
                  {isConfettiActive && <ReactConfetti tweenDuration={2000} />}
                </div>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </Card>
  );
}

export default PurchaseCustomizeFormPage;
